import React, { useState, useEffect } from "react";
// TEMPORARY: Disable debug imports to fix initialization error
// import { toastDebug, getReactDiagnostics, shouldLog } from "../utils/debug";
const toastDebug = { log: () => {}, track: () => {}, error: () => {}, trackError: () => {} };
const shouldLog = () => false;
const getReactDiagnostics = () => ({ reactAvailable: false, environment: "production" });

// Safe debug initialization - defer until actually needed
let debugInitialized = false;

const safeInitializeDebug = () => {
  if (debugInitialized) return;

  try {
    if (typeof shouldLog === "function" && shouldLog && shouldLog()) {
      toastDebug?.log("Environment debugging", getReactDiagnostics());

      // Try to make React available globally if it's not
      if (typeof window !== "undefined" && !window.React && React) {
        toastDebug?.log("Setting React on window object");
        window.React = React;
      }
      debugInitialized = true;
    }
  } catch (error) {
    // Silently handle debug initialization errors - don't log to avoid recursion
    debugInitialized = true; // Mark as initialized to avoid retry loops
  }
};

const TOAST_LIMIT = 1;
const TOAST_REMOVE_DELAY = 1000000;

const actionTypes = {
  ADD_TOAST: "ADD_TOAST",
  UPDATE_TOAST: "UPDATE_TOAST",
  DISMISS_TOAST: "DISMISS_TOAST",
  REMOVE_TOAST: "REMOVE_TOAST",
};

let count = 0;

function genId() {
  count = (count + 1) % Number.MAX_SAFE_INTEGER;
  return count.toString();
}

const toastTimeouts = new Map();

const addToRemoveQueue = (toastId) => {
  if (toastTimeouts.has(toastId)) {
    return;
  }

  const timeout = setTimeout(() => {
    toastTimeouts.delete(toastId);
    dispatch({
      type: actionTypes.REMOVE_TOAST,
      toastId: toastId,
    });
  }, TOAST_REMOVE_DELAY);

  toastTimeouts.set(toastId, timeout);
};

const reducer = (state, action) => {
  switch (action.type) {
    case actionTypes.ADD_TOAST:
      return {
        ...state,
        toasts: [action.toast, ...state.toasts].slice(0, TOAST_LIMIT),
      };

    case actionTypes.UPDATE_TOAST:
      return {
        ...state,
        toasts: state.toasts.map((t) =>
          t.id === action.toast.id ? { ...t, ...action.toast } : t,
        ),
      };

    case actionTypes.DISMISS_TOAST: {
      const { toastId } = action;

      if (toastId) {
        addToRemoveQueue(toastId);
      } else {
        state.toasts.forEach((toast) => {
          addToRemoveQueue(toast.id);
        });
      }

      return {
        ...state,
        toasts: state.toasts.map((t) =>
          t.id === toastId || toastId === undefined
            ? {
                ...t,
                open: false,
              }
            : t,
        ),
      };
    }
    case actionTypes.REMOVE_TOAST:
      if (action.toastId === undefined) {
        return {
          ...state,
          toasts: [],
        };
      }
      return {
        ...state,
        toasts: state.toasts.filter((t) => t.id !== action.toastId),
      };
  }
};

const listeners = [];
let memoryState = { toasts: [] };

function dispatch(action) {
  memoryState = reducer(memoryState, action);
  listeners.forEach((listener) => {
    listener(memoryState);
  });
}

function toast({ ...props }) {
  const id = genId();

  const update = (props) =>
    dispatch({
      type: actionTypes.UPDATE_TOAST,
      toast: { ...props, id },
    });
  const dismiss = () =>
    dispatch({ type: actionTypes.DISMISS_TOAST, toastId: id });

  dispatch({
    type: actionTypes.ADD_TOAST,
    toast: {
      ...props,
      id,
      open: true,
      onOpenChange: (open) => {
        if (!open) dismiss();
      },
    },
  });

  return {
    id: id,
    dismiss,
    update,
  };
}

function useToast() {
  // Safe debug initialization on first use
  safeInitializeDebug();

  // Debug environment and React availability (with safe check)
  try {
    if (typeof toastDebug?.track === "function") {
      toastDebug.track({
        memoryStateKeys: Object.keys(memoryState),
        listenersCount: listeners.length,
        reactInfo: getReactDiagnostics(),
      });
    }
  } catch (error) {
    // Silently skip debug tracking if it fails
  }

  // Always call hooks unconditionally
  const [state, setState] = useState(memoryState);

  useEffect(() => {
    toastDebug.log("useEffect setting up listener", {
      listenersCount: listeners.length,
    });
    listeners.push(setState);
    return () => {
      toastDebug.log("useEffect cleanup", {
        listenersCount: listeners.length - 1,
      });
      const index = listeners.indexOf(setState);
      if (index > -1) {
        listeners.splice(index, 1);
      }
    };
  }, [state, setState]);

  // Handle any errors after hooks are called
  if (typeof useState !== "function") {
    const error = new Error(
      `useState is not a function, got: ${typeof useState}`,
    );
    try {
      toastDebug?.trackError?.(error);
      toastDebug?.error?.("Error with useState function", {
        error: error.message,
        stack: error.stack,
        memoryState,
      });
    } catch (debugError) {
      // Silently skip debug logging if it fails
    }
  }

  try {
    toastDebug?.log?.("useState successful", { state });
  } catch (debugError) {
    // Silently skip debug logging if it fails
  }

  const result = {
    ...state,
    toast,
    dismiss: (toastId) =>
      dispatch({ type: actionTypes.DISMISS_TOAST, toastId }),
  };

  try {
    toastDebug?.log?.("Returning result", {
      hasToast: typeof result.toast,
      hasDismiss: typeof result.dismiss,
    });
  } catch (debugError) {
    // Silently skip debug logging if it fails
  }

  return result;
}

export { useToast, toast };
